import React, { useEffect, useState } from "react";
import { Button, Icon } from "@material-ui/core";
import { useFormik } from "formik";
import * as Yup from "yup";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../../stores/helpers/useStore";

import SubscriptionDetails from "./SubscriptionDetails";
import PricingTable from "./PricingTable";
import PriceCalculator from "./PriceCalculator";
import SubscriptionDescription from "./SubscriptionDescription";
import CancelSubscriptionModal from "../../../../components/Modals/CancelSubscriptionModal/CancelSubscriptionModal";
import ErrorModal from "../../../../components/Modals/ErrorModal/ErrorModal";
import { apiErrorMessage } from "../../../../helpers/apiErrorMessage";
import "../../Subscription.css";

const initialValues = {
  calculateHosts: 0,
};

const validationSchema = Yup.object().shape({
  hostsQuantity: Yup.number("Must be a number").test(
    "Min",
    "Minimum number is: 3",
    (num) => num >= 3 && num !== 0
  ),
  calculateHosts: Yup.number("Must be a number").test(
    "Min",
    "Minimum number is: 3",
    (num) => num >= 3
  ),
});

const ViewSubscription = ({
  subscriptionQuantity,
  nextCycleQuantity,
  subscriptionEnd,
  subscriptionStatus,
  createSubscription,
  updateSubscription,
  cancelAdminSubscription,
  subscriptionApprovalLink,
  subscriptionSource,
  billingStatus,
  notice,
  clearNotice,
  openBillingPortal,
  getPrice,
}) => {
  const [expectedPrice, setExpectedPrice] = useState(0);
  const [openCancelSubModal, setOpenCancelSubModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscriptionCanceled, setSubscriptionCanceled] = useState(false);
  const [reason, setReason] = useState("");
  const [showCalculator, setShowCalculator] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [actionError, setActionError] = useState("");
  const [billingLoading, setBillingLoading] = useState(false);

  const {
    subscriptionStore: { easterEggActive, toggleEasterEgg },
  } = useStore();

  const formik = useFormik({
    initialValues,
    validationSchema,
  });

  const cancelSubscription = async () => {
    try {
      setLoading(true);
      await cancelAdminSubscription(reason);
      setLoading(false);
      setSubscriptionCanceled(true);
    } catch (err) {
      setLoading(false);
      setOpenCancelSubModal(false);
      setActionError(
        apiErrorMessage(err, "Couldn't cancel the subscription. Try again in a moment.")
      );
    }
  };

  const manageBilling = async () => {
    try {
      setBillingLoading(true);
      const response = await openBillingPortal(false);
      if (response?.url) {
        window.location.href = response.url;
        return;
      }
      setActionError("Billing didn't return a link. Try again in a moment.");
    } catch (err) {
      setActionError(
        apiErrorMessage(err, "Couldn't open billing. Try again in a moment.")
      );
    } finally {
      setBillingLoading(false);
    }
  };

  // Easter egg bypass handler
  const handleEasterEgg = () => {
    const password = prompt("Enter password:");
    if (password === "keyresults") {
      toggleEasterEgg();
      if (!easterEggActive) {
        alert("Account upgraded! 🎉");
      } else {
        alert("Account downgraded to free version.");
      }
    } else if (password !== null) {
      alert("Incorrect password.");
    }
  };

  // calculating expected price of subscription based on user input
  useEffect(() => {
    // if (formik.errors.calculateHosts) {
    //   return;
    // }
    setExpectedPrice(getPrice(formik.values.calculateHosts));
  }, [
    // formik.errors.calculateHosts,
    formik.values.calculateHosts,
    getPrice,
  ]);

  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <h1>Subscription Management</h1>
        <p className="subscription-header-subtitle">
          Manage your TriviaRat Enterprise subscription and <span 
            onDoubleClick={handleEasterEgg}
            style={{ cursor: 'default', userSelect: 'none' }}
          >billing</span>
        </p>
      </div>

      {subscriptionStatus && subscriptionStatus === "INACTIVE" && !easterEggActive && (
        <div className="subscription-empty-state">
          <Icon className="subscription-empty-icon">card_membership</Icon>
          <h2 className="subscription-empty-title">No Active Subscription</h2>
          <p className="subscription-empty-description">
            Start your TriviaRat Enterprise subscription to unlock host management and premium features.
          </p>
          <Button
            variant="contained"
            color="primary"
            className="subscription-button primary"
            onClick={createSubscription}
            startIcon={<Icon>add_circle</Icon>}
          >
            Create Subscription
          </Button>
        </div>
      )}

      {subscriptionStatus && (subscriptionStatus !== "INACTIVE" || easterEggActive) && (
        <>
          <SubscriptionDetails
            subscriptionQuantity={easterEggActive ? 10 : subscriptionQuantity}
            subscriptionEnd={easterEggActive ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() : subscriptionEnd}
            nextCycleQuantity={easterEggActive ? 10 : nextCycleQuantity}
            subscriptionStatus={easterEggActive ? "ACTIVE" : subscriptionStatus}
            subscriptionSource={easterEggActive ? "stripe" : subscriptionSource}
            getPrice={getPrice}
          />

          {notice && (
            <div className="subscription-info-banner standalone">
              <Icon className="subscription-info-banner-icon">check_circle</Icon>
              <div className="subscription-info-banner-text">
                {notice}
                <button type="button" className="subscription-inline-dismiss" onClick={clearNotice}>
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {subscriptionSource === "stripe" &&
            ["past_due", "unpaid", "incomplete"].includes(billingStatus) && (
            <div className="subscription-info-banner standalone">
              <Icon className="subscription-info-banner-icon">error_outline</Icon>
              <div className="subscription-info-banner-text">
                The latest card payment hasn't gone through. Update the card before changing the host count.
              </div>
            </div>
          )}

          {subscriptionApprovalLink && (
            <div className="subscription-info-banner standalone">
              <Icon className="subscription-info-banner-icon">info</Icon>
              <div className="subscription-info-banner-text">
                Your subscription is awaiting approval. Please click the button below to complete the setup.
              </div>
            </div>
          )}

          <div className="subscription-actions">
            {subscriptionApprovalLink && (
              <Button
                href={subscriptionApprovalLink}
                variant="contained"
                color="primary"
                className="subscription-button primary"
                startIcon={<Icon>check_circle</Icon>}
              >
                Approve Subscription
              </Button>
            )}
            {subscriptionStatus !== "CANCELED" && !subscriptionApprovalLink && !easterEggActive && (
              <Button
                variant="contained"
                color="primary"
                className="subscription-button primary"
                onClick={updateSubscription}
                startIcon={<Icon>edit</Icon>}
              >
                Update Subscription
              </Button>
            )}
            {subscriptionSource === "stripe" && !easterEggActive && (
              <Button
                variant="outlined"
                className="subscription-button"
                onClick={manageBilling}
                disabled={billingLoading}
                startIcon={<Icon>credit_card</Icon>}
              >
                {billingLoading ? "Opening billing..." : "Manage billing"}
              </Button>
            )}
          </div>
        </>
      )}

      <PricingTable
        subscriptionStatus={subscriptionStatus}
        subscriptionSource={subscriptionSource}
      />

      {/* Price Calculator - Collapsible */}
      <div className="subscription-collapsible">
        <button
          className="subscription-collapsible-trigger"
          onClick={() => setShowCalculator(!showCalculator)}
          type="button"
        >
          <div className="subscription-collapsible-trigger-content">
            <Icon className="subscription-collapsible-trigger-icon">calculate</Icon>
            <div className="subscription-collapsible-trigger-text">
              <h3>Price Calculator</h3>
              <p>Estimate your monthly subscription cost</p>
            </div>
          </div>
          <Icon className={`subscription-collapsible-chevron ${showCalculator ? 'expanded' : ''}`}>
            expand_more
          </Icon>
        </button>
        <div className={`subscription-collapsible-content ${showCalculator ? 'expanded' : ''}`}>
          <PriceCalculator
            expectedPrice={expectedPrice}
            value={formik.values.calculateHosts}
            error={!!formik.errors.calculateHosts}
            helperText={formik.errors.calculateHosts}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      </div>

      {/* How It Works - Collapsible */}
      <div className="subscription-collapsible">
        <button
          className="subscription-collapsible-trigger"
          onClick={() => setShowHowItWorks(!showHowItWorks)}
          type="button"
        >
          <div className="subscription-collapsible-trigger-content">
            <Icon className="subscription-collapsible-trigger-icon">help_outline</Icon>
            <div className="subscription-collapsible-trigger-text">
              <h3>How It Works</h3>
              <p>Learn about subscription billing and management</p>
            </div>
          </div>
          <Icon className={`subscription-collapsible-chevron ${showHowItWorks ? 'expanded' : ''}`}>
            expand_more
          </Icon>
        </button>
        <div className={`subscription-collapsible-content ${showHowItWorks ? 'expanded' : ''}`}>
          <SubscriptionDescription subscriptionSource={subscriptionSource} />
        </div>
      </div>

      {subscriptionStatus === "ACTIVE" && !easterEggActive && (
        <div className="subscription-actions">
          <Button
            variant="outlined"
            className="subscription-button danger"
            onClick={() => setOpenCancelSubModal(true)}
            startIcon={<Icon>cancel</Icon>}
          >
            Cancel Subscription
          </Button>
        </div>
      )}

      <CancelSubscriptionModal
        open={openCancelSubModal}
        reason={reason}
        loading={loading}
        subscriptionCanceled={subscriptionCanceled}
        setReason={setReason}
        cancel={cancelSubscription}
        handleClose={() => setOpenCancelSubModal(false)}
      />

      <ErrorModal
        open={!!actionError}
        title="Something went wrong"
        errorText={actionError}
        handleClose={() => setActionError("")}
      />
    </div>
  );
};

export default observer(ViewSubscription);
